import { useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { notifications } from '@mantine/notifications';
import { Button, ControlledInput, ControlledTextarea, ControlledFileInput } from '@shared/ui';
import { ProductFilters, type ProductFiltersValues } from '@components';
import { useCreateFilterSet, useUpdateFilterSet } from '../../hooks/useFilterSets';
import { filterSetFormSchema } from '../../utils/filterSetValidation';
import type { FilterSet, UpdateFilterSetDto } from '../../types';
import './FilterSetForm.css';

interface FilterSetFormProps {
  filterSet?: FilterSet;
  onSuccess?: () => void;
}

type FileInputValue = File | { id: number; url: string };

interface FilterSetFormData {
  title: string;
  description?: string;
  image?: FileInputValue | null;
}

export default function FilterSetForm({ filterSet, onSuccess }: FilterSetFormProps) {
  const isEditMode = !!filterSet;
  const resolver = yupResolver(filterSetFormSchema) as any;

  const { handleSubmit, formState: { errors }, reset, control } = useForm<FilterSetFormData>({
    resolver,
    defaultValues: {
      title: filterSet?.title || '',
      description: filterSet?.description || '',
      image: filterSet?.imageUrl ? { id: 0, url: filterSet.imageUrl } : null,
    },
  });
  const createFilterSet = useCreateFilterSet();
  const updateFilterSet = useUpdateFilterSet();

  const [filterValues, setFilterValues] = useState<ProductFiltersValues>({
    colorIds: filterSet?.filters?.colorIds || [],
    flowerTypeIds: filterSet?.filters?.flowerTypeIds || [],
    assemblyTypeIds: filterSet?.filters?.assemblyTypeIds || [],
    minPrice: filterSet?.filters?.minPrice?.toString() || '',
    maxPrice: filterSet?.filters?.maxPrice?.toString() || '',
  });

  useEffect(() => {
    if (filterSet) {
      reset({
        title: filterSet.title,
        description: filterSet.description || '',
        image: filterSet.imageUrl ? { id: 0, url: filterSet.imageUrl } : null,
      });
      setFilterValues({
        colorIds: filterSet.filters?.colorIds || [],
        flowerTypeIds: filterSet.filters?.flowerTypeIds || [],
        assemblyTypeIds: filterSet.filters?.assemblyTypeIds || [],
        minPrice: filterSet.filters?.minPrice?.toString() || '',
        maxPrice: filterSet.filters?.maxPrice?.toString() || '',
      });
    }
  }, [filterSet, reset]);

  const onSubmit = async (data: FilterSetFormData) => {
    try {
      const imageFile = data.image instanceof File ? data.image : undefined;

      // Преобразуем значения фильтров в JSON объект
      const filters: Record<string, any> = {};

      if (filterValues.colorIds.length > 0) {
        filters.colorIds = filterValues.colorIds;
      }
      if (filterValues.flowerTypeIds.length > 0) {
        filters.flowerTypeIds = filterValues.flowerTypeIds;
      }
      if (filterValues.assemblyTypeIds.length > 0) {
        filters.assemblyTypeIds = filterValues.assemblyTypeIds;
      }
      if (filterValues.minPrice.trim()) {
        const min = parseFloat(filterValues.minPrice);
        if (!isNaN(min)) {
          filters.minPrice = min;
        }
      }
      if (filterValues.maxPrice.trim()) {
        const max = parseFloat(filterValues.maxPrice);
        if (!isNaN(max)) {
          filters.maxPrice = max;
        }
      }

      if (isEditMode && filterSet) {
        const updateData: UpdateFilterSetDto = {
          title: data.title,
          description: data.description || undefined,
          filters,
        };

        // Определяем, было ли изображение удалено
        // Если изначально было изображение, а сейчас null - значит удалено
        const hadImage = filterSet.imageUrl && filterSet.imageUrl.length > 0;
        const imageRemoved = hadImage && data.image === null;

        if (imageRemoved) {
          updateData.shouldDeleteImage = true;
        }

        await updateFilterSet.mutateAsync({
          id: filterSet.id,
          data: updateData,
          file: imageFile,
        });
      } else {
        await createFilterSet.mutateAsync({
          data: {
            title: data.title,
            description: data.description || undefined,
            filters,
          },
          file: imageFile,
        });
      }

      notifications.show({
        title: 'Успешно',
        message: `Подборка по фильтрам успешно ${isEditMode ? 'обновлена' : 'создана'}`,
        color: 'green',
      });

      onSuccess?.();
    } catch (error) {
      notifications.show({
        title: 'Ошибка',
        message: `Ошибка при ${isEditMode ? 'обновлении' : 'создании'} подборки по фильтрам`,
        color: 'red',
      });
    }
  };

  return (
    <form onSubmit={handleSubmit(onSubmit)} className="filter-set-form">
      <div className="filter-set-form__content">
        <div className="form-group">
          <ControlledInput
            id="title"
            type="text"
            label="Название *"
            name="title"
            control={control}
            error={errors.title}
          />
        </div>

        <div className="form-group">
          <ControlledTextarea
            id="description"
            rows={4}
            label="Описание"
            name="description"
            control={control}
            error={errors.description}
          />
        </div>

        <div className="form-group">
          <label>Фильтры</label>
          <ProductFilters
            values={filterValues}
            onChange={setFilterValues}
            showSearch={false}
            showClearButton={true}
          />
        </div>

        <div className="form-group">
          <ControlledFileInput
            id="image"
            name="image"
            label="Обложка"
            accept="image/*"
            control={control}
            error={errors.image}
          />
        </div>
      </div>

      <div className="filter-set-form__footer">
        <Button
          type="submit"
          loading={createFilterSet.isPending || updateFilterSet.isPending}
          className="filter-set-form__submit-button"
        >
          {isEditMode ? 'Обновить подборку' : 'Создать подборку'}
        </Button>
      </div>
    </form>
  );
}

